import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";
import { getAccessTokenPayload, getCookie, signOut } from "../helper/APIClient";
import { MenuSub } from "../helper/interface";
import { useMenu } from "../context/useMenu";
import { usePackage } from "../context/usePackage";
import { useProfile } from "../context/ProfileContext";
import { useChatWidget } from "../context/useChatWidget";
import { dispatchPageTour } from "../helper/pageTour";
import FeedbackPopup from "./FeedbackPopup";
import ProfileIcon from "../icons/profile.svg?react";
import EmailIcon from "../icons/email.svg?react";
import PackageIcon from "../icons/package.svg?react";
import ProfileAffiliationSummary from "./ProfileAffiliationSummary";

interface PortalDropdownProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  align?: "left" | "center" | "right";
  minWidth?: number;
  offsetX?: number;
}

const PortalDropdown: React.FC<PortalDropdownProps> = ({
  anchorRef,
  children,
  align = "left",
  minWidth = 280,
  offsetX = 0,
}) => {
  const [style, setStyle] = useState<React.CSSProperties>({ position: "absolute", top: 0, left: 0, zIndex: 9999 });

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    let left = rect.left + window.scrollX;
    if (align === "center") left = rect.left + window.scrollX + rect.width / 2 - minWidth / 2;
    if (align === "right") left = rect.right + window.scrollX - minWidth;

    left += offsetX;
    left = Math.max(8, Math.min(left, window.innerWidth + window.scrollX - minWidth - 8));

    setStyle({
      position: "absolute",
      top: rect.bottom + window.scrollY + 8,
      left,
      minWidth,
      zIndex: 9999,
    });
  }, [align, anchorRef, minWidth, offsetX]);

  return ReactDOM.createPortal(
    <div style={style} className="portal-dropdown-content">{children}</div>,
    document.body
  );
};

const LEGACY_MENU_ROUTE_MAP: Record<string, string> = {
  "/nourl12": "/probate-exports",
};

const ProfileMenuControl = () => {
  const { menus, hasAccess } = useMenu();
  const {
    currentPackage,
  } = usePackage();
  const { profile, avatarUrl } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const chatWidget = useChatWidget();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [loadings, setLoading] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const profileMenuAnchorRef = useRef<HTMLButtonElement>(null);

  const email = profile?.email || decodeURIComponent(getCookie("email") ?? "");
  const fullName = profile?.fullName || decodeURIComponent(getCookie("fullName") ?? "");
  const profileMenus = menus.filter((m) => m.isProfile === true);

  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "FS";

  const adminLevel = useMemo(() => {
    const payload = getAccessTokenPayload();
    const raw = payload?.["admin_level"] ?? payload?.["adminLevel"];
    const parsed = typeof raw === "number" ? raw : typeof raw === "string" ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const currentPath = location.pathname.toLowerCase();
  const isPageTourAvailable =
    currentPath === "/" ||
    currentPath === "/executor-management" ||
    currentPath === "/executor-portal" ||
    currentPath === "/probate-manager" ||
    currentPath === "/vault-administration" ||
    currentPath === "/reminders";

  const resolveMenuActionUri = (actionUri?: string | null) => {
    if (!actionUri) return null;
    const normalizedActionUri = actionUri.startsWith("/") ? actionUri : `/${actionUri}`;
    return LEGACY_MENU_ROUTE_MAP[normalizedActionUri.toLowerCase()] ?? normalizedActionUri;
  };

  const isLegacyMenuRoute = (actionUri?: string | null) => {
    if (!actionUri) return false;
    const normalizedActionUri = actionUri.startsWith("/") ? actionUri : `/${actionUri}`;
    return normalizedActionUri.toLowerCase() in LEGACY_MENU_ROUTE_MAP;
  };

  const isSpecialMenuAction = (actionUri?: string | null) => {
    const resolvedActionUri = resolveMenuActionUri(actionUri);
    if (!resolvedActionUri) return false;
    const uri = resolvedActionUri.toLowerCase();
    return uri.includes("/help/") || uri === "/feedback" || uri.includes("nourl") || uri.includes("comingsoon");
  };

  const canNavigateToMenuAction = (actionUri?: string | null) => {
    const normalized = resolveMenuActionUri(actionUri);
    if (!normalized) return false;
    if (isSpecialMenuAction(actionUri) || isLegacyMenuRoute(actionUri)) return true;
    if (normalized.toLowerCase() === "/partner") return hasAccess("/partner");
    if ((normalized.toLowerCase() === "/admin/referral-partners" || normalized.toLowerCase() === "/admin/partner-programmes") && adminLevel < 50) {
      return false;
    }
    return hasAccess(normalized);
  };

  const buildMenuDisabledTitle = (actionUri?: string | null) => {
    const normalized = resolveMenuActionUri(actionUri);
    if (!normalized) return "Unavailable.";
    if (isLegacyMenuRoute(actionUri)) return null;
    if (normalized.toLowerCase() === "/partner") return hasAccess("/partner") ? null : "You don't have access to this page.";
    if ((normalized.toLowerCase() === "/admin/referral-partners" || normalized.toLowerCase() === "/admin/partner-programmes") && adminLevel < 50) {
      return "Partner administration is only available for admin level 50+ users.";
    }
    return "You don't have access to this page.";
  };

  const handleMenuAction = async (actionUri?: string | null) => {
    const normalizedActionUri = resolveMenuActionUri(actionUri);
    if (!normalizedActionUri) return;
    const uri = normalizedActionUri.toLowerCase();

    if (uri.includes("/help/")) {
      chatWidget.expand();
      setIsProfileOpen(false);
      return;
    }

    if (uri === "/feedback") {
      setShowFeedbackPopup(true);
      setIsProfileOpen(false);
      return;
    }

    if (uri.includes("nourl") || uri.includes("comingsoon")) {
      setIsProfileOpen(false);
      return;
    }

    setIsProfileOpen(false);
    navigate(normalizedActionUri);
  };

  const toggleProfile = async () => {
    if (!localStorage.getItem("salt")) {
      await signOut();
      navigate("/login", { replace: true });
      return;
    }
    setIsProfileOpen((prev) => !prev);
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut();
      navigate("/logout", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest(".portal-dropdown-content") || target.closest(".profile-controls")) {
        return;
      }
      setIsProfileOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="profile-controls">
      <button
        type="button"
        className="profile-avatar"
        ref={profileMenuAnchorRef}
        onClick={(event) => { event.stopPropagation(); void toggleProfile(); }}
        aria-label="Open profile menu"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="Profile" className="profile-avatar-image" />
        ) : (
          <div className="profile-avatar-fallback" aria-hidden="true">{initials}</div>
        )}
      </button>

      <button
        type="button"
        className="profile-menu-trigger"
        onClick={(event) => { event.stopPropagation(); void toggleProfile(); }}
        aria-label="More profile options"
      >
        <span className="profile-menu-trigger-dot" />
        <span className="profile-menu-trigger-dot" />
        <span className="profile-menu-trigger-dot" />
      </button>

      {isProfileOpen && (
        <PortalDropdown anchorRef={profileMenuAnchorRef as React.RefObject<HTMLElement>} align="right" minWidth={300} offsetX={-80}>
          <div className="profile-dropdown" style={{ position: "static" }} onClick={(event) => event.stopPropagation()}>
            <div className="profile-dropdown-header">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Profile" className="profile-dropdown-avatar" />
              ) : (
                <div className="profile-dropdown-avatar-fallback">{initials}</div>
              )}
              <div className="profile-details">
                <h3><ProfileIcon className="profile-icon" /><b>{fullName}</b></h3>
                <h3><EmailIcon className="profile-icon" /><b>{email}</b></h3>
                <h3><PackageIcon className="profile-icon" /><b>Package: {currentPackage?.packageName || "No package"}</b></h3>
                <ProfileAffiliationSummary profile={profile} />
              </div>
            </div>
            <hr />
            <div className="profile-submenu-scroll">
              {[...profileMenus]
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                .map((profileMenu) => (
                profileMenu.subMenus && profileMenu.subMenus.length > 0 && (
                  <div key={profileMenu.id} className="profile-menu-items">
                    {[...profileMenu.subMenus]
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map((sub: MenuSub) => {
                        const isPageTourItem = sub.label.trim().toLowerCase() === "page tour";
                        const isPageTourDisabled = isPageTourItem && !isPageTourAvailable;
                        const isAccessDisabled = !isPageTourItem && !canNavigateToMenuAction(sub.actionUri);
                        const isDisabled = isPageTourDisabled || isAccessDisabled;
                        const disabledTitle = isPageTourDisabled
                          ? "Available only on Dashboard, Executor, Probate, Vault Admin, and Reminders pages."
                          : isAccessDisabled
                            ? buildMenuDisabledTitle(sub.actionUri)
                            : undefined;

                        return (
                          <button
                            key={sub.id}
                            className={`profile-menu-btn ${isDisabled ? "profile-menu-btn--disabled" : ""}`}
                            aria-disabled={isDisabled}
                            title={disabledTitle ?? undefined}
                            onClick={() => {
                              if (isDisabled) return;
                              if (isPageTourItem) {
                                dispatchPageTour();
                                setIsProfileOpen(false);
                                return;
                              }
                              void handleMenuAction(sub.actionUri);
                            }}
                          >
                            {sub.label}
                          </button>
                        );
                      })}
                  </div>
                )
              ))}
            </div>
            <button className="sign-out-btn" onClick={handleSignOut}>
              {loadings ? "Signing out..." : "sign out"}
            </button>
          </div>
        </PortalDropdown>
      )}

      <FeedbackPopup
        show={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        userName={fullName}
        userEmail={email}
      />
    </div>
  );
};

export default ProfileMenuControl;
